import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { api } from '../api/client';
import Loading from '../components/Loading';

const LiveAttendance = () => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchAllEvents();

    // Auto-refresh every 10 seconds if enabled
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchAllEvents();
        if (selectedEvent) {
          fetchEventDetails(selectedEvent._id);
        }
      }, 10000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, selectedEvent]);

  const fetchAllEvents = async () => {
    try {
      const data = await api.get('/admin/events/attendance-summary');
      setEvents(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error(error.message || 'Failed to load events');
      setLoading(false);
    }
  };

  const fetchEventDetails = async (eventId) => {
    setDetailsLoading(true);
    try {
      const data = await api.get(`/admin/events/${eventId}/attendance`);
      setSelectedEvent(data.event);
      setAttendees(data.attendees);
      setDetailsLoading(false);
    } catch (error) {
      console.error('Error fetching event details:', error);
      toast.error(error.message || 'Failed to load event details');
      setDetailsLoading(false);
    }
  };

  const handleEventClick = (event) => {
    fetchEventDetails(event._id);
  };

  const handleRefresh = () => {
    fetchAllEvents();
    if (selectedEvent) {
      fetchEventDetails(selectedEvent._id);
    }
    toast.success('Refreshed successfully');
  };

  const filteredAttendees = attendees.filter((attendee) =>
    attendee.attendeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    attendee.attendeeEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
    attendee.ticketNumber.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <Loading />;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Live Attendance Monitoring</h1>
          <p className="text-gray-600 mt-1">Real-time attendance tracking for all events</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
          <label className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-4 h-4"
            />
            <span className="text-sm text-gray-700">Auto-refresh (10s)</span>
          </label>
        </div>
      </div>

      {/* Events Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {events.map((event) => (
          <div
            key={event._id}
            onClick={() => handleEventClick(event)}
            className={`bg-white rounded-lg shadow p-4 cursor-pointer transition hover:shadow-lg ${selectedEvent?._id === event._id ? 'ring-2 ring-blue-500' : ''
              }`}
          >
            <h3 className="font-semibold text-lg text-gray-900 mb-2">{event.name}</h3>
            <p className="text-sm text-gray-600 mb-3">
              <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {event.venue}
            </p>

            {/* Stats Grid */}
            <div className="grid grid-cols-5 gap-2 mt-3">
              <div className="text-center p-2 bg-purple-50 rounded">
                <div className="text-xs text-purple-600 font-medium">Total</div>
                <div className="text-lg font-bold text-purple-700">{event.seatsTotal}</div>
              </div>
              <div className="text-center p-2 bg-blue-50 rounded">
                <div className="text-xs text-blue-600 font-medium">Sold</div>
                <div className="text-lg font-bold text-blue-700">{event.stats.ticketsSold}</div>
              </div>
              <div className="text-center p-2 bg-orange-50 rounded">
                <div className="text-xs text-orange-600 font-medium">Left</div>
                <div className="text-lg font-bold text-orange-700">
                  {event.seatsTotal - event.stats.ticketsSold}
                </div>
              </div>
              <div className="text-center p-2 bg-green-50 rounded">
                <div className="text-xs text-green-600 font-medium">Present</div>
                <div className="text-lg font-bold text-green-700">{event.stats.present}</div>
              </div>
              <div className="text-center p-2 bg-red-50 rounded">
                <div className="text-xs text-red-600 font-medium">Absent</div>
                <div className="text-lg font-bold text-red-700">{event.stats.absent}</div>
              </div>
            </div>

            {/* Attendance Progress Bar */}
            <div className="mt-3">
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>Attendance</span>
                <span className="font-semibold">{event.stats.attendancePercentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all"
                  style={{ width: `${event.stats.attendancePercentage}%` }}
                ></div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Event Details Modal */}
      {/* Event Details Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
              onClick={() => setSelectedEvent(null)}
            ></div>

            {/* Modal panel */}
            <div className="relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="mb-4 flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900">{selectedEvent.name}</h2>
                  <button
                    onClick={() => setSelectedEvent(null)}
                    className="text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Search Bar */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by name, email, or ticket number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Attendees Table */}
                {detailsLoading ? (
                  <Loading />
                ) : (
                  <div className="overflow-x-auto max-h-[60vh]">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Ticket #
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Phone
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Check-in Time
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredAttendees.map((attendee) => (
                          <tr key={attendee.orderId} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {attendee.ticketNumber}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {attendee.attendeeName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {attendee.attendeeEmail}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {attendee.attendeePhone || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${attendee.status === 'present'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                                  }`}
                              >
                                {attendee.status.toUpperCase()}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {attendee.checkedInAt
                                ? new Date(attendee.checkedInAt).toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                                : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {filteredAttendees.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        No attendees found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {events.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No events found</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating a new event.</p>
        </div>
      )}
    </div>
  );
};

export default LiveAttendance;
